// The estate — the organizations, customers and sites the caller can see —
// read once per session and again on demand, and one copy for everything
// that names a scope (ZENITH.md §18, #192). The rail names the scope it
// shows from it; a form offers scopes from it; a select lists parents from
// it. Before this each of those drained the three collections for itself, so
// a Users page with three role panels open read the estate five times.
//
// It is not a cache of server state in general (that is #198's question). It
// is one shared reader for one shape of data, refreshed on the rail's open
// and after any organization, customer or site write — §18's "looks names up
// live rather than caching them" is honoured by refreshing at those moments,
// never by trusting a copy across a session.
import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { api } from "../api/client";
import { drain } from "../api/hooks";
import { describe, type Problem } from "../api/problem";
import type { components } from "../api/schema";
import { useSession } from "./session";

type Org = components["schemas"]["Org"];
type Customer = components["schemas"]["Customer"];
type Site = components["schemas"]["Site"];

/** The three lists, and whether any of them was cut at the drain's bound
 * (#188): past it a scope may be real and unlisted, and every reader says so. */
export interface Lists { orgs: Org[]; customers: Customer[]; sites: Site[]; more: boolean }

/** Drains the three collections. The provider below is its one caller. */
async function loadEstate(): Promise<Lists> {
  const [o, c, s] = await Promise.all([
    drain<Org>((t) => api.GET("/orgs", { params: { query: { page_size: 100, page_token: t } } })),
    drain<Customer>((t) => api.GET("/customers", { params: { query: { page_size: 100, page_token: t } } })),
    drain<Site>((t) => api.GET("/sites", { params: { query: { page_size: 100, page_token: t } } })),
  ]);
  return { orgs: o.items, customers: c.items, sites: s.items, more: o.more || c.more || s.more };
}

/** The lists with the rows read one at a time added, each once. A row the
 * drain did load wins: it is the newer read of the same row. */
function withAdopted(l: Lists, a: { customers: Customer[]; sites: Site[] }): Lists {
  if (a.customers.length === 0 && a.sites.length === 0) return l;
  return {
    ...l,
    customers: [...l.customers, ...a.customers.filter((c) => !l.customers.some((x) => x.id === c.id))],
    sites: [...l.sites, ...a.sites.filter((s) => !l.sites.some((x) => x.id === s.id))],
  };
}

interface Estate {
  lists?: Lists;
  /** A read is out and nothing has answered yet this session. */
  loading: boolean;
  /** The last read did not reach the server (#245). The same Problem
   * `useQuery` reports for a fetch that throws, so a reader shows it the
   * way every page does rather than loading forever. Cleared by the next
   * read. A page that answered with an error is `drain`'s to report and is
   * not this: it returns what it read and says no more. */
  problem?: Problem;
  /** Reads the estate again, when it has been read at all. A copy nobody has
   * asked for is not refreshed: the first reader gets a fresh one. */
  refresh(): void;
  /** Says a reader is on screen; the first one starts the read. */
  ensure(): void;
  /** Reads one scope the lists did not load and adds it to them (#728). The
   * lists are cut at the drain's bound (#188), and a scope found past it by
   * `GET /v1/search` is picked without ever having been in them, so nothing
   * that names a scope from this copy could name it. The row is read by its
   * own id, the way the lists would have served it, and is kept across this
   * session's refreshes. A read that fails adds nothing: the scope is still
   * picked, and is named "…" as any unlisted scope past the bound is. */
  adopt(scope: { customerId?: string; siteId?: string }): void;
}

const Ctx = createContext<Estate | undefined>(undefined);

export function EstateProvider({ children }: { children: ReactNode }) {
  const { me } = useSession();
  const who = me?.user.id;
  const [lists, setLists] = useState<Lists>();
  const [loading, setLoading] = useState(false);
  const [problem, setProblem] = useState<Problem>();
  // Which read is current, and whether one was ever started for this person.
  const latest = useRef(0);
  const started = useRef<string | undefined>(undefined);
  // The rows read one at a time for scopes found by search (`adopt`). They
  // are merged into every copy of the lists, so a refresh does not drop them.
  const adopted = useRef<{ customers: Customer[]; sites: Site[] }>({ customers: [], sites: [] });

  const load = useCallback(() => {
    const id = ++latest.current;
    setLoading(true);
    setProblem(undefined);
    loadEstate().then((l) => {
      if (id !== latest.current) return;
      setLists(withAdopted(l, adopted.current));
      setLoading(false);
    }, () => {
      // A fetch that threw: the request did not reach the server. Without
      // this branch it was an unhandled rejection and the reader loaded
      // forever (#245).
      if (id !== latest.current) return;
      setProblem(describe(undefined));
      setLoading(false);
    });
  }, []);

  // Unmounted: whatever is still in flight answers nobody, and the person is
  // forgotten with it, so a remount's `ensure` starts a read of its own rather
  // than waiting on the one this just invalidated. StrictMode mounts, unmounts
  // and mounts again (main.tsx); without the second half the rail read "…" and
  // the menu "Loading…" for the whole of every development session (#253).
  useEffect(() => () => { latest.current++; started.current = undefined; }, []);

  // Another person signed in: their estate is not this one's. The copy is
  // dropped, and readers on screen ask again because `ensure` changes.
  useEffect(() => {
    if (started.current !== undefined && started.current !== who) {
      started.current = undefined;
      latest.current++;
      adopted.current = { customers: [], sites: [] };
      setLists(undefined);
      setLoading(false);
    }
  }, [who]);

  const ensure = useCallback(() => {
    if (started.current === who) return;
    started.current = who;
    load();
  }, [load, who]);
  const refresh = useCallback(() => { if (started.current !== undefined) load(); }, [load]);

  const adopt = useCallback((scope: { customerId?: string; siteId?: string }) => {
    const whose = started.current;
    const keep = (rows: { customers?: Customer[]; sites?: Site[] }) => {
      // Another person signed in while the row was being read: not theirs.
      if (started.current !== whose) return;
      const a = adopted.current;
      adopted.current = {
        customers: [...a.customers.filter((c) => !rows.customers?.some((n) => n.id === c.id)), ...(rows.customers ?? [])],
        sites: [...a.sites.filter((s) => !rows.sites?.some((n) => n.id === s.id)), ...(rows.sites ?? [])],
      };
      setLists((l) => (l ? withAdopted(l, adopted.current) : l));
    };
    // Each read is the row's own, under the caller's scope: a customer the
    // caller cannot read answers 404 and is left out, which is what a person
    // granted at one site gets for that site's customer (§6.3).
    if (scope.siteId) void api.GET("/sites/{id}", { params: { path: { id: scope.siteId } } }).then(({ data }) => { if (data) keep({ sites: [data] }); }, () => undefined);
    if (scope.customerId) void api.GET("/customers/{id}", { params: { path: { id: scope.customerId } } }).then(({ data }) => { if (data) keep({ customers: [data] }); }, () => undefined);
  }, []);

  const value = useMemo<Estate>(() => ({ lists, loading, problem, refresh, ensure, adopt }), [lists, loading, problem, refresh, ensure, adopt]);
  return <Ctx.Provider value={value}>{children}</Ctx.Provider>;
}

/** The shared estate. `enabled` says whether this reader needs it yet — a
 * form that has not been opened does not — so a page that never asks never
 * costs the three reads; once anything on screen has asked, every reader
 * holds the same copy. `loading` is true only while this reader needs it,
 * it has not arrived, and the read has not failed; `problem` is why it will
 * not arrive, when that is so. */
/** The estate's lists where there is an estate and it has answered, asking
 * for nothing: for a component drawn inside the estate and outside it alike,
 * such as an error banner (#2033). */
export function useEstateIfAny(): Lists | undefined {
  return useContext(Ctx)?.lists;
}

export function useEstate(enabled = true): { lists?: Lists; loading: boolean; problem?: Problem; refresh(): void; adopt: Estate["adopt"] } {
  const e = useContext(Ctx);
  if (!e) throw new Error("useEstate outside EstateProvider");
  const { ensure } = e;
  useEffect(() => { if (enabled) ensure(); }, [enabled, ensure]);
  return { lists: e.lists, loading: enabled && !e.lists && !e.problem, problem: e.problem, refresh: e.refresh, adopt: e.adopt };
}

/** What the scope on screen is called: the site, the customer above it, and
 * the organization above that (#1168).
 *
 * **Why a page needs this at all.** The scope is three ids in the address and
 * a per-device write takes a serial, so a page can send a configuration to a
 * device with nothing on screen naming whose device it is. Two serials at two
 * customers differing in their last two characters is enough: on 2026-09-30 an
 * operator applied a carried document to `d4babaa25458` at Hugg Inn intending
 * `d4babaa254a0` at Hubie Lab, with the serial written out in front of them.
 *
 * It costs no request: the estate is the copy the scope rail already reads,
 * and this is the same lookup `ScopePicker` makes for its own label.
 *
 * `known` is false when the estate does not name the scope — it was cut at the
 * drain's bound (#188), or the read has not answered. A caller then says so
 * and does not present an id as a name, and never presents nothing as though
 * the question did not arise (conventions/dashboards-as-code.md).
 */
export interface ScopeNames { org?: string; customer?: string; site?: string; known: boolean }

export function scopeNames(lists: Lists | undefined, scope: { orgId?: string; customerId?: string; siteId?: string }): ScopeNames {
  if (!lists) return { known: false };
  const site = scope.siteId ? lists.sites.find((s) => s.id === scope.siteId) : undefined;
  const customerId = site?.customer_id ?? scope.customerId;
  const customer = customerId ? lists.customers.find((c) => c.id === customerId) : undefined;
  const orgId = customer?.org_id ?? scope.orgId;
  const org = orgId ? lists.orgs.find((o) => o.id === orgId) : undefined;
  // Known when everything the scope names was found. A site in scope and no
  // site row is not partially known: it is the case this flag exists for.
  const known = (!scope.siteId || !!site) && (!customerId || !!customer) && (!orgId || !!org);
  return { org: org?.name, customer: customer?.name, site: site?.name, known };
}
